import { useEffect, useRef, useState } from "react";

/**
 * Pancha gold particle shimmer.
 * All particles spawn at the BOTTOM of their container and rise upward continuously,
 * with a subtle ±10px horizontal sway, fading in at bottom and out at top.
 *
 * variant:
 *  - "violet"  bright shimmer for dark/violet sections (80 dots, 2-5px, opacity 0.7-1.0, 4-8s)
 *  - "cream"   subtle shimmer for cream/light sections (40 dots, 1-3px, opacity 0.3-0.5, 6-12s)
 *
 * Mobile (<768px): particle count auto-reduced by 40%.
 * Performance: CSS-only animation, transform-based, pointer-events none.
 */

const VARIANTS = {
  violet: {
    count: 80,
    sizes: [2, 3, 4, 5],
    opacityRange: [0.7, 1.0],
    durationRange: [12, 20],
    cls: "pp-violet",
  },
  cream: {
    count: 40,
    sizes: [1, 2, 3],
    opacityRange: [0.3, 0.5],
    durationRange: [18, 28],
    cls: "pp-cream",
  },
};

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randIn([min, max]) {
  return min + Math.random() * (max - min);
}

function generateNodes(targetCount, cfg) {
  const arr = [];
  for (let i = 0; i < targetCount; i++) {
    const duration = randIn(cfg.durationRange);
    arr.push({
      i,
      size: pick(cfg.sizes),
      left: Math.random() * 100,
      duration,
      // delay distributed across full duration so flow feels endless from frame 0
      delay: -Math.random() * duration,
      peak: randIn(cfg.opacityRange),
      sway: (Math.random() < 0.5 ? -1 : 1) * (12 + Math.random() * 8), // ±12-20px
    });
  }
  return arr;
}

export default function Particles({ variant = "violet", count, className = "" }) {
  const cfg = VARIANTS[variant] || VARIANTS.violet;

  const isMobile =
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

  const targetCount = Math.max(
    4,
    Math.round((count ?? cfg.count) * (isMobile ? 0.6 : 1))
  );

  const ref = useRef(null);
  // Particle positions/sizes/timing are generated ONCE per mount via a lazy useState
  // initializer. variant + count are static at every call site, so we don't need to
  // regenerate at runtime. Lazy initialiser keeps render pure.
  const [nodes] = useState(() => generateNodes(targetCount, cfg));

  // Measure parent height and write directly to a CSS custom property on the
  // ref'd element — avoids round-tripping through React state.
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const measure = () => {
      const h = el.parentElement?.clientHeight || el.clientHeight || 800;
      el.style.setProperty("--pp-rise", `${h + 80}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`pp-layer ${cfg.cls} ${className}`}
      aria-hidden
      style={{ "--pp-rise": "1200px" }}
    >
      {nodes.map((n) => (
        <span
          key={n.i}
          className="pp-dot"
          style={{
            left: `${n.left}%`,
            width: `${n.size}px`,
            height: `${n.size}px`,
            animationDuration: `${n.duration}s`,
            animationDelay: `${n.delay}s`,
            "--pp-peak": n.peak,
            "--pp-sway": `${n.sway}px`,
          }}
        />
      ))}
    </div>
  );
}
